import React from "react";
import { useFormik } from "formik";
import { Language, ConferenceConfiguration } from "../types/conference.types";
import { ParticipantTitleType } from "../types/participant.types";
import { AddressType } from "../types/address.types";
import { RegistrationRequest } from "../types/registration.types";
import { getIntervalInDays, formatDateForInput } from "../utils/date-formatter";
import { getCorrectDate } from "../utils/date-utils";
import { formatTitle } from "../utils/type-formatter";
import { useRoomsQuery } from "./useRoomsQuery";
import { useRegisterMutation } from "../store/participant/participant";
import { Conference } from "../types/conference.types";

interface UsePreviewFormParams {
  congressEvent: Conference | null;
  congressId: number;
  initialValues?: RegistrationRequest;
  isEditMode: boolean;
  formikRef?: React.RefObject<{ submitForm: () => void }>;
  registrationConfigJson?: ConferenceConfiguration;
  onEditSubmitSuccess?: (values: RegistrationRequest) => void;
}

export const usePreviewForm = ({
  congressEvent,
  congressId,
  initialValues,
  isEditMode,
  formikRef,
  registrationConfigJson,
  onEditSubmitSuccess,
}: UsePreviewFormParams) => {
  const [register, registerState] = useRegisterMutation();

  const createDefaultPayment = React.useCallback(() => {
    return {
      payment: undefined,
      city: "",
      street: "",
      postalCode: "",
      country:
        congressEvent?.language === Language.HUNGARIAN ? "Magyarország" : "",
      taxNumber: "",
      email: "",
      phone: "",
      fullName: "",
      valuesByFieldId: new Map<number, string | number>(),
    };
  }, [congressEvent?.language]);

  const getDefaultInitialValues = React.useCallback((): RegistrationRequest => {
    return {
      participantRegister: {
        title: ParticipantTitleType.NONE,
        firstName: "",
        surName: "",
        workplace: "",
        email: "",
        valuesByFieldId: new Map<number, string | number>(),
      },
      address: {
        addressType: undefined as unknown as AddressType,
        city: "",
        street: "",
        postalCode: "",
        country:
          congressEvent?.language === Language.HUNGARIAN ? "Magyarország" : "",
        taxNumber: "",
        phone: "",
        valuesByFieldId: new Map<number, string | number>(),
      },
      payment: congressEvent?.configJson.paymentDetailsEnabled
        ? createDefaultPayment()
        : null,
      meals: congressEvent?.configJson.lunchEnabled ? [] : null,
      professionals: congressEvent?.configJson.activityEnabled ? [] : null,
      socials: congressEvent?.configJson.activityEnabled ? [] : null,
      hotelBooking: congressEvent?.configJson.accommodationEnabled
        ? {
            hotelId: 0,
            roomId: undefined,
            arrivalDate: formatDateForInput(
              getCorrectDate(
                congressEvent.startDate,
                congressEvent.bookingStartDate,
              ),
            ),
            departureDate: formatDateForInput(
              getCorrectDate(
                congressEvent.endDate,
                congressEvent.bookingEndDate,
                false,
              ),
            ),
            noNights: getIntervalInDays(
              getCorrectDate(
                congressEvent.startDate,
                congressEvent.bookingStartDate,
              ),
              getCorrectDate(
                congressEvent.endDate,
                congressEvent.bookingEndDate,
                false,
              ),
            ),
            roomMate: "",
          }
        : null,
      presentations: congressEvent?.configJson.presentationEnabled ? [] : null,
      participantFee: congressEvent?.configJson.participantFeeEnabled
        ? { participationFeeId: undefined }
        : null,
      escortFee: congressEvent?.configJson.participantFeeEnabled
        ? {
            participationFeeId: undefined,
            numberOfAttendants: 0,
            attendantsName: "",
          }
        : null,
      congressEventId: congressId,
      comment: "",
    };
  }, [congressEvent, congressId, createDefaultPayment]);

  const normalizedInitialValues = React.useMemo((): RegistrationRequest => {
    const defaultInitialValues = getDefaultInitialValues();
    if (!initialValues) return defaultInitialValues;

    if (congressEvent?.configJson.paymentDetailsEnabled) {
      return {
        ...initialValues,
        payment: initialValues.payment ?? createDefaultPayment(),
      };
    }

    return {
      ...initialValues,
      payment: null,
    };
  }, [
    congressEvent?.configJson.paymentDetailsEnabled,
    createDefaultPayment,
    getDefaultInitialValues,
    initialValues,
  ]);

  const formik = useFormik({
    initialValues: normalizedInitialValues,
    validate: (values: RegistrationRequest) => {
      const errors: any = {};

      if (!values.participantRegister?.firstName?.trim()) {
        errors.participantRegister = {
          ...errors.participantRegister,
          firstName: "Required",
        };
      }
      if (!values.participantRegister?.surName?.trim()) {
        errors.participantRegister = {
          ...errors.participantRegister,
          surName: "Required",
        };
      }
      if (!values.participantRegister?.workplace?.trim()) {
        errors.participantRegister = {
          ...errors.participantRegister,
          workplace: "Required",
        };
      }
      if (!values.participantRegister?.email?.trim()) {
        errors.participantRegister = {
          ...errors.participantRegister,
          email: "Required",
        };
      }

      // Soft required validation for mailing address
      if (
        isEditMode &&
        registrationConfigJson &&
        congressEvent?.configJson?.mailingDetailsEnabled
      ) {
        const isMailingSoftRequired =
          !registrationConfigJson.mailingDetailsEnabled &&
          congressEvent.configJson.mailingDetailsEnabled;

        if (isMailingSoftRequired) {
          // Only validate if user has started filling the module
          // Note: country might have default value, so only count it if addressType is also selected
          const hasAnyFieldFilled = !!(
            (values.address?.addressType &&
              values.address.addressType !== AddressType.NONE) ||
            values.address?.city?.trim() ||
            values.address?.street?.trim() ||
            values.address?.postalCode?.trim() ||
            values.address?.phone?.trim()
          );

          if (
            hasAnyFieldFilled &&
            (!values.address?.addressType ||
              values.address.addressType === AddressType.NONE)
          ) {
            errors.address = {
              ...errors.address,
              addressType: "Required",
            };
          }
        } else {
          // Normal required validation
          if (
            !values.address?.addressType ||
            values.address.addressType === AddressType.NONE
          ) {
            errors.address = {
              ...errors.address,
              addressType: "Required",
            };
          }
        }
      } else if (
        congressEvent?.configJson?.mailingDetailsEnabled &&
        (!values.address?.addressType ||
          values.address.addressType === AddressType.NONE)
      ) {
        // Normal required validation for non-edit mode or when module was always enabled
        errors.address = {
          ...errors.address,
          addressType: "Required",
        };
      }

      // Soft required validation for billing / payment details
      if (
        isEditMode &&
        registrationConfigJson &&
        congressEvent?.configJson?.paymentDetailsEnabled
      ) {
        const isBillingSoftRequired =
          !registrationConfigJson.paymentDetailsEnabled &&
          congressEvent.configJson.paymentDetailsEnabled;

        if (isBillingSoftRequired) {
          const hasAnyPaymentFieldFilled = !!(
            values.payment?.payment ||
            values.payment?.fullName?.trim() ||
            values.payment?.city?.trim() ||
            values.payment?.street?.trim() ||
            values.payment?.postalCode?.trim() ||
            values.payment?.country?.trim() ||
            values.payment?.email?.trim() ||
            values.payment?.phone?.trim() ||
            values.payment?.taxNumber?.trim()
          );

          if (hasAnyPaymentFieldFilled && !values.payment?.payment) {
            errors.payment = {
              ...errors.payment,
              payment: "Required",
            };
          }
        } else {
          if (!values.payment?.payment) {
            errors.payment = {
              ...errors.payment,
              payment: "Required",
            };
          }
        }
      } else if (
        congressEvent?.configJson?.paymentDetailsEnabled &&
        !values.payment?.payment
      ) {
        errors.payment = {
          ...errors.payment,
          payment: "Required",
        };
      }

      // Soft required validation for participant fee (attendance fee)
      if (
        isEditMode &&
        registrationConfigJson &&
        congressEvent?.configJson?.participantFeeEnabled
      ) {
        const isParticipantFeeSoftRequired =
          !registrationConfigJson.participantFeeEnabled &&
          congressEvent.configJson.participantFeeEnabled;
        if (!isParticipantFeeSoftRequired) {
          if (
            !values.participantFee?.participationFeeId ||
            values.participantFee.participationFeeId === 0
          ) {
            errors.participantFee = {
              ...errors.participantFee,
              participationFeeId: "Required",
            };
          }
        }
      } else if (
        congressEvent?.configJson?.participantFeeEnabled &&
        (!values.participantFee?.participationFeeId ||
          values.participantFee.participationFeeId === 0)
      ) {
        errors.participantFee = {
          ...errors.participantFee,
          participationFeeId: "Required",
        };
      }

      // Soft required validation for escort fee
      if (
        isEditMode &&
        registrationConfigJson &&
        congressEvent?.configJson?.participantFeeEnabled
      ) {
        const isEscortFeeSoftRequired =
          !registrationConfigJson.participantFeeEnabled &&
          congressEvent.configJson.participantFeeEnabled;
        const hasEscortFeeFilled = !!(
          (values.escortFee?.numberOfAttendants ?? 0) > 0 ||
          values.escortFee?.attendantsName?.trim()
        );
        if (isEscortFeeSoftRequired) {
          if (hasEscortFeeFilled) {
            if ((values.escortFee?.numberOfAttendants ?? 0) > 0) {
              if (!values.escortFee?.attendantsName?.trim()) {
                errors.escortFee = {
                  ...errors.escortFee,
                  attendantsName: "Required",
                };
              }
            }
          }
        } else if (
          congressEvent.configJson.participantFeeEnabled &&
          values.escortFee &&
          (values.escortFee.numberOfAttendants ?? 0) > 0 &&
          !values.escortFee.attendantsName?.trim()
        ) {
          errors.escortFee = {
            ...errors.escortFee,
            attendantsName: "Required",
          };
        }
      }

      // Soft required validation for presentations
      if (
        isEditMode &&
        registrationConfigJson &&
        congressEvent?.configJson?.presentationEnabled
      ) {
        const isPresentationSoftRequired =
          !registrationConfigJson.presentationEnabled &&
          congressEvent.configJson.presentationEnabled;
        const presentations = values.presentations ?? [];

        if (isPresentationSoftRequired) {
          const hasAnyPresentationFieldFilled =
            presentations.length > 0 ||
            presentations.some(
              (p) =>
                !!(
                  (p.presentationTypeId && p.presentationTypeId !== 0) ||
                  (p.presentationTopicId && p.presentationTopicId !== 0) ||
                  p.presentationTitle?.trim() ||
                  p.performers?.trim() ||
                  p.performersWorkplace?.trim()
                ),
            );

          if (hasAnyPresentationFieldFilled && presentations.length > 0) {
            const presentationErrors: Array<{
              presentationTypeId?: string;
              presentationTopicId?: string;
              presentationTitle?: string;
            }> = [];
            presentations.forEach((p, idx) => {
              const item: (typeof presentationErrors)[0] = {};
              if (!p.presentationTypeId || p.presentationTypeId === 0)
                item.presentationTypeId = "Required";
              if (!p.presentationTopicId || p.presentationTopicId === 0)
                item.presentationTopicId = "Required";
              if (!p.presentationTitle?.trim())
                item.presentationTitle = "Required";
              if (Object.keys(item).length) presentationErrors[idx] = item;
            });
            if (presentationErrors.length) {
              errors.presentations =
                presentationErrors as typeof errors.presentations;
            }
          }
        } else {
          if (presentations.length > 0) {
            const presentationErrors: Array<{
              presentationTypeId?: string;
              presentationTopicId?: string;
              presentationTitle?: string;
            }> = [];
            presentations.forEach((p, idx) => {
              const item: (typeof presentationErrors)[0] = {};
              if (!p.presentationTypeId || p.presentationTypeId === 0)
                item.presentationTypeId = "Required";
              if (!p.presentationTopicId || p.presentationTopicId === 0)
                item.presentationTopicId = "Required";
              if (!p.presentationTitle?.trim())
                item.presentationTitle = "Required";
              if (Object.keys(item).length) presentationErrors[idx] = item;
            });
            if (presentationErrors.length) {
              errors.presentations =
                presentationErrors as typeof errors.presentations;
            }
          }
        }
      } else if (
        congressEvent?.configJson?.presentationEnabled &&
        (values.presentations?.length ?? 0) > 0
      ) {
        const presentations = values.presentations!;
        const presentationErrors: Array<{
          presentationTypeId?: string;
          presentationTopicId?: string;
          presentationTitle?: string;
        }> = [];
        presentations.forEach((p, idx) => {
          const item: (typeof presentationErrors)[0] = {};
          if (!p.presentationTypeId || p.presentationTypeId === 0)
            item.presentationTypeId = "Required";
          if (!p.presentationTopicId || p.presentationTopicId === 0)
            item.presentationTopicId = "Required";
          if (!p.presentationTitle?.trim()) item.presentationTitle = "Required";
          if (Object.keys(item).length) presentationErrors[idx] = item;
        });
        if (presentationErrors.length) {
          errors.presentations =
            presentationErrors as typeof errors.presentations;
        }
      }

      return errors;
    },
    onSubmit: (values: RegistrationRequest) => {
      if (isEditMode) {
        onEditSubmitSuccess?.(values);
        return;
      } else {
        register({
          ...values,
          hotelBooking:
            congressEvent?.configJson.accommodationEnabled &&
            values.hotelBooking?.hotelId
              ? values.hotelBooking
              : null,
          congressEventId: congressId,
        });
      }
    },
    enableReinitialize: true,
  });

  React.useImperativeHandle(
    formikRef,
    () => ({
      submitForm: () => formik.handleSubmit(),
      getValues: () => formik.values,
      setValues: (values: RegistrationRequest) => formik.setValues(values),
      setFieldValue: (field: string, value: any) =>
        formik.setFieldValue(field, value),
      validateForm: () => formik.validateForm(),
      setTouched: (touched: any) => formik.setTouched(touched),
    }),
    [formik],
  );

  const hotelId = formik.values.hotelBooking?.hotelId;
  const { rooms } = useRoomsQuery(congressId, isEditMode, hotelId);

  const onCopy = React.useCallback(() => {
    const title = formik.values.participantRegister.title;
    const firstName = formik.values.participantRegister.firstName;
    const surName = formik.values.participantRegister.surName;
    const formattedTitle = title ? formatTitle(title) : "";
    const fullName = formattedTitle
      ? `${formattedTitle} ${firstName} ${surName}`
      : `${firstName} ${surName}`;
    formik.setFieldValue("payment.fullName", fullName);
    formik.setFieldValue(
      "payment.country",
      formik.values.address?.country ?? "",
    );
    formik.setFieldValue(
      "payment.postalCode",
      formik.values.address?.postalCode ?? "",
    );
    formik.setFieldValue("payment.city", formik.values.address?.city ?? "");
    formik.setFieldValue("payment.street", formik.values.address?.street ?? "");
    formik.setFieldValue(
      "payment.email",
      formik.values.participantRegister.email,
    );
    formik.setFieldValue("payment.phone", formik.values.address?.phone ?? "");
  }, [formik]);

  React.useEffect(() => {
    if (
      !isEditMode &&
      congressId &&
      (!formik.values.hotelBooking?.hotelId ||
        isNaN(parseInt(formik.values.hotelBooking?.hotelId + "")))
    ) {
      formik.setFieldValue("hotelBooking.hotelId", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, congressId, formik.values.hotelBooking?.hotelId]);

  React.useEffect(() => {
    formik.setFieldValue(
      "hotelBooking.noNights",
      getIntervalInDays(
        formik.values.hotelBooking?.arrivalDate,
        formik.values.hotelBooking?.departureDate,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.hotelBooking?.arrivalDate,
    formik.values.hotelBooking?.departureDate,
  ]);

  return {
    formik,
    rooms,
    onCopy,
    registerState,
  };
};
